import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, apiPatch, apiDelete, operationsClient } from '@/lib/api';
import { settingsEndpoints } from '@/lib/api/endpoints';
import { settingsKeys } from '@/lib/queryKeys';

/**
 * Which documents terms are for: sales terms go on estimates, jobs, invoices
 * and proposals; purchase terms on purchase orders. Each has its own default.
 */
export type TermsType = 'SALES' | 'PURCHASE';

export const termsTypeLabels: Record<TermsType, string> = {
  SALES: 'Sales',
  PURCHASE: 'Purchase',
};

export interface TermsTemplate {
  id: string;
  tenantId: string;
  name: string;
  content: string;
  /** Fixed once created. */
  type: TermsType;
  /** At most one per type; records set to "Default" resolve to this one. */
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

/** The terms of one type (sales unless asked), or every type the viewer may see. */
export function useTermsTemplates(type: TermsType | 'ALL' = 'SALES') {
  return useQuery({
    queryKey: [...settingsKeys.termsTemplates(), type],
    queryFn: () =>
      apiGet<TermsTemplate[]>(operationsClient, settingsEndpoints.termsTemplates(), {
        params: { type },
      }),
  });
}

export function useCreateTermsTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: { name: string; content: string; type?: TermsType; isDefault?: boolean }) =>
      apiPost<TermsTemplate>(operationsClient, settingsEndpoints.termsTemplates(), dto),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: settingsKeys.termsTemplates() });
    },
  });
}

export function useUpdateTermsTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...dto
    }: {
      id: string;
      name?: string;
      content?: string;
      isDefault?: boolean;
    }) => apiPatch<TermsTemplate>(operationsClient, settingsEndpoints.termsTemplateDetail(id), dto),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: settingsKeys.termsTemplates() });
    },
  });
}

/**
 * Promote a template to the tenant default. The incumbent is demoted
 * server-side — a tenant with templates always has exactly one default.
 */
export function useSetDefaultTermsTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiPost<TermsTemplate>(operationsClient, settingsEndpoints.termsTemplateDefault(id), {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: settingsKeys.termsTemplates() });
    },
  });
}

export function useDeleteTermsTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiDelete(operationsClient, settingsEndpoints.termsTemplateDetail(id)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: settingsKeys.termsTemplates() });
    },
  });
}
